/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import type { CommunitySectionFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";
import { ReactNode } from "react";

import { css } from "@panda/css";
import { HStack, Stack, styled, VStack } from "@panda/jsx";
import { flex, grid } from "@panda/patterns";

import type { CommunityForumRowProps } from "@components/elements";
import {
	BackgroundContainer,
	CollapsibleCardViewport,
	CommunityCard,
	CommunityForumRow,
	CosmosButton,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
	EdsIcon,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";
import { toShortNum } from "@components/utils/numberFormatters";

export type CommunitySectionProps = CommunitySectionFragment & {
	discussionCategory?: {
		total_topics_weekly_count: number;
		categories: CommunityForumRowProps[];
	};
};

export const CommunitySection = ({
	config,
	discussionCategory,
	forumTitle,
	forumButton,
	resources,
	skipCommunityTable,
}: CommunitySectionProps): ReactNode => {
	const messages = useMessages();
	const forumFullTitle =
		forumTitle?.indexOf("{postsPerWeek}") !== -1
			? forumTitle?.replace(
					"{postsPerWeek}",
					`(${toShortNum(discussionCategory?.total_topics_weekly_count || 0)} ${messages["epic.cosmos.postsPerWeek"]})`,
				)
			: forumTitle;

	const cardsExist = resources?.cards?.length && resources.cards.length > 0;
	const resourcesCardsMax = Math.max(cardsExist ? resources.cards.length : 5, 3);
	const titleExists = !!forumFullTitle;
	const buttonExists = !!forumButton;
	const categoriesExist = discussionCategory?.categories?.length && discussionCategory.categories.length > 0;

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)}>
					<VStack gap="0" alignItems="flex-start" className="community-section">
						{config?.heading?.desc ? (
							<HStack alignItems="center">
								<div className={flex({ alignItems: "stretch", justifyContent: "stretch", aspectRatio: "1 / 1" })}>
									<EdsIcon icon="EpicGames" size="lg" />
								</div>
								{config?.heading?.desc ? (
									<CosmosRichText variant={CosmosTextVariantEnum.headingSM} html={config?.heading?.desc} />
								) : null}
							</HStack>
						) : null}
						{config?.heading?.title?.html ? <styled.div height="eds.dimension.24" /> : null}
						<SectionHeading {...config?.heading} desc={null} mobileTextAlignStart />
						{cardsExist ? (
							<>
								<styled.div height={{ base: "eds.dimension.32" }} />
								<CollapsibleCardViewport
									viewportCss={grid.raw({
										columnGap: "eds.dimension.16",
										rowGap: "eds.dimension.16",
										columns: { base: resourcesCardsMax, xlDown: 3, mdDown: 2, smDown: 1 },
										w: "full",
									})}
								>
									{resources?.cards?.map((data) => (
										<CommunityCard key={data.title} {...data} />
									))}
								</CollapsibleCardViewport>
							</>
						) : null}
						{titleExists || buttonExists ? (
							<>
								<styled.div height={{ base: "eds.dimension.32" }} />
								<HStack gap="eds.dimension.6" width="100%" alignItems="flex-end">
									{titleExists ? (
										<Stack
											gap="eds.dimension.6"
											width="100%"
											alignItems={{ base: "flex-end", smDown: "flex-start" }}
											direction={{ base: "row", smDown: "column" }}
										>
											<CosmosText
												as="h3"
												className={css({ marginRight: "auto" })}
												variant={CosmosTextVariantEnum.headingLG}
											>
												{forumFullTitle}
											</CosmosText>
										</Stack>
									) : null}
									{buttonExists ? <CosmosButton {...forumButton} /> : null}
								</HStack>
							</>
						) : null}
						{categoriesExist && !skipCommunityTable ? (
							<>
								<styled.div height="eds.dimension.24" />
								<VStack
									gap="eds.dimension.2"
									className={css({
										w: "full",
									})}
								>
									{discussionCategory?.categories?.map((data: CommunityForumRowProps, i: number) => (
										<CommunityForumRow
											key={data.name}
											{...data}
											className={css({
												borderTop: i ? "none" : undefined,
												borderBottom: i !== discussionCategory?.categories?.length - 1 ? "none" : undefined,
												roundedTop: i ? 0 : "eds.borderRadius.16",
												roundedBottom: i === discussionCategory?.categories?.length - 1 ? "eds.borderRadius.16" : 0,
											})}
										/>
									))}
								</VStack>
							</>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
